import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Calendar, ChevronRight, BookOpen, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const CATEGORY_COLORS = {
  Guida: "bg-blue-50 text-blue-700",
  Risparmio: "bg-emerald-50 text-emerald-700",
  Tecnologia: "bg-purple-50 text-purple-700",
  Incentivi: "bg-amber-50 text-amber-700",
  Consigli: "bg-rose-50 text-rose-700",
};

export default function BlogPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);

  useSEO({
    title: "Blog Fotovoltaico | Guide, Consigli e Novita | Fotovoltaico Chiavi in Mano",
    description: "Guide, consigli e novita sul fotovoltaico. Scopri come risparmiare con l'energia solare, gli incentivi disponibili e le ultime tecnologie.",
    canonical: `${window.location.origin}/blog`,
  });

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.append("category", activeCategory);
        if (searchQuery.trim()) params.append("search", searchQuery.trim());
        const qs = params.toString();
        const res = await axios.get(`${API}/blog/articles${qs ? `?${qs}` : ""}`);
        setArticles(res.data);
      } catch {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [activeCategory, searchQuery]);

  const categories = ["Guida", "Risparmio", "Tecnologia", "Incentivi", "Consigli"];

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-14 w-auto object-contain" />
            <span className="hidden sm:block text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Fotovoltaico Chiavi in Mano
            </span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 transition-colors" data-testid="blog-back">
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </button>
            <button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg shadow-emerald-500/25" data-testid="blog-cta">
              Richiedi Preventivo
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-slate-50 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-emerald-100/30 rounded-full -translate-y-1/3 translate-x-1/3" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Blog</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="blog-title">
            Il Magazine del <span className="text-emerald-500">Fotovoltaico</span>
          </h1>
          <p className="mt-4 text-lg text-slate-500 max-w-2xl">
            Guide, consigli e novita per chi vuole passare all'energia solare. Tutto quello che devi sapere, spiegato semplice.
          </p>
        </div>
      </section>

      {/* Search + Category filter */}
      <div className="border-b border-slate-100 bg-white sticky top-20 z-20" data-testid="blog-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 py-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca articoli..."
                className="pl-10 h-9 rounded-full text-sm bg-slate-50 border-slate-200"
                data-testid="blog-search-input"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              <button
                onClick={() => setActiveCategory("")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${!activeCategory ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid="blog-category-all"
              >
                Tutti
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${activeCategory === cat ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`blog-category-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <section className="py-12 sm:py-16" data-testid="blog-articles-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-500">Caricamento articoli...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-no-articles">
              <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <p className="text-lg font-semibold text-slate-400">Nessun articolo trovato</p>
              <p className="text-sm text-slate-400 mt-1">Torna presto per nuovi contenuti!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, i) => (
                <article
                  key={article.id}
                  className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                  onClick={() => navigate(`/blog/${article.slug}`)}
                  data-testid={`blog-card-${article.slug}`}
                >
                  {article.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={article.cover_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {article.category && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[article.category] || "bg-slate-100 text-slate-600"}`}>
                          {article.category}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(article.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {article.title}
                    </h2>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    <span className="text-sm font-semibold text-emerald-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                      Leggi l'articolo <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-center">
        <p className="text-xs text-slate-500">© 2025 Fotovoltaico Chiavi in Mano. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}
